import {
  downloadAttachment,
  downloadAttachmentUrl,
  executeDqlQuery,
  executeLegacyFindByIdQuery,
  executeLegacyFindQuery,
  executeLegacyRemove,
  executeLegacyUpdate,
  executeLegacyUpsert,
  executeRemoteQuery,
  getCollectionDocumentCount,
  isEmptyCollection,
  rpcLogin,
  uploadAttachment,
} from '@portal/core/api'
import {
  type DQLHTTPResponse,
  type ExecuteDQLParams,
  type LegacyFindByIdParams,
  type LegacyFindByIdResponse,
  type LegacyFindParams,
  type LegacyFindResponse,
  type LegacyRemoveParams,
  type LegacyUpdateParams,
  type LegacyUpsertParams,
  type RemoteQueryHTTPResponse,
} from '@portal/core/types'
import type * as Sentry from '@sentry/browser'
import { useAppContext } from 'context'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getRpcAuthParams } from 'utils/rpcAuth'
import { captureWithContext } from 'utils/sentry/tracking'

/**
 * A basic type guard to check if a given value is a valid Sentry severity level
 */
const isSentryLevel = (level: unknown): level is Sentry.SeverityLevel => {
  if (typeof level !== 'string') return false
  return (
    [
      'fatal',
      'error',
      'warning',
      'info',
      'debug',
      'log',
    ] satisfies Sentry.SeverityLevel[]
  ).includes(level as Sentry.SeverityLevel)
}

/** Hook used by the portal to interact with an app's RPC API. */
const useRpcClient = (appId: string, appDomain?: string) => {
  // We need to keep the access token in state for the return value of this hook,
  // and in a ref to avoid recreating the isEmptyCollectionThroughRpc callback
  // unnecessarily, so that it can be safely placed in dependency arrays.
  const [rpcAccessToken, setRpcAccessToken] = useState<string>()
  const rpcAccessTokenRef = useRef<string>()
  const { jwtToken } = useAppContext()
  const tokenRenewalTimeout = useRef<number>()
  // Preserved to minimize the SDK decouple footprint. Could later surface
  // async failures like rpcLogin rejections.
  const error: Error | undefined = undefined

  /**
   * Captures RPC errors with enhanced categorization and tagging for Sentry
   */
  const captureRpcError = useCallback(
    (error: unknown, context: Record<string, unknown>) => {
      captureWithContext(
        error,
        {
          component: 'useRpcClient',
          operation: context.operation,
          databaseId: appId,
          errorType: context.errorType,
          statusCode: context.httpStatus,
          durationMs: context.executionTimeMs,
        },
        isSentryLevel(context.level) ? context.level : 'error',
      )
    },
    [appId],
  )

  const renewRpcToken = async () => {
    if (!appDomain) return

    const { token, provider } = await getRpcAuthParams(jwtToken)

    const { accessToken, tokenExpiration } = await rpcLogin(
      appDomain,
      appId,
      provider,
      token,
    )

    const expiresIn = dayjs(tokenExpiration).diff()

    setRpcAccessToken(accessToken)
    rpcAccessTokenRef.current = accessToken

    tokenRenewalTimeout.current = window?.setTimeout(
      renewRpcToken,
      expiresIn - 60000,
    )
  }

  useEffect(() => {
    renewRpcToken()

    return () => {
      if (tokenRenewalTimeout.current) {
        clearTimeout(tokenRenewalTimeout.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!appDomain])

  const isEmptyCollectionThroughRpc = useCallback(
    (collectionName: string, query: string) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? isEmptyCollection(
            collectionName,
            query,
            appId,
            appDomain,
            accessToken,
          )
        : Promise.resolve(false)
    },
    [appId, appDomain],
  )

  const getCollectionDocumentCountThroughRpc = useCallback(
    (collectionName: string, query = 'true') => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? getCollectionDocumentCount(
            collectionName,
            query,
            appId,
            appDomain,
            accessToken,
          )
        : Promise.resolve(0)
    },
    [appId, appDomain],
  )

  const executeLegacyFindQueryThroughRpc = useCallback(
    (params: LegacyFindParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyFindQuery(params, appDomain, appId, accessToken)
        : Promise.resolve({
            documents: [],
          } as unknown as LegacyFindResponse)
    },
    [appId, appDomain],
  )

  const executeLegacyFindByIdQueryThroughRpc = useCallback(
    (params: LegacyFindByIdParams): Promise<LegacyFindByIdResponse | null> => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyFindByIdQuery(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyUpdateThroughRpc = useCallback(
    (params: LegacyUpdateParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyUpdate(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyUpsertThroughRpc = useCallback(
    (params: LegacyUpsertParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyUpsert(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyRemoveThroughRpc = useCallback(
    (params: LegacyRemoveParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyRemove(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const downloadAttachmentThroughRpc = useCallback(
    (id: string) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? downloadAttachment(id, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const attachmentBaseURL = useMemo(
    () => (appDomain ? downloadAttachmentUrl(appDomain, appId) : ''),
    [appDomain, appId],
  )

  const uploadAttachmentThroughRpc = useCallback(
    (file: File) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? uploadAttachment(file, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeDqlQueryThroughRpc = useCallback(
    (query: string, args?: ExecuteDQLParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeDqlQuery(query, appDomain, appId, accessToken, args, {
            onError: captureRpcError,
          })
        : Promise.resolve({
            queryType: 'unknown',
            items: [],
            error: {},
            warnings: [],
          } as DQLHTTPResponse)
    },
    [appId, appDomain, captureRpcError],
  )

  // A variant of captureRpcError that treats user-submitted bad DQL (400 with a
  // dql_error response) as a debug-level event instead of an alert. This is to
  // cut the noise a bit in #portal-alerts, since we don't need error-level reporting
  // on bad user queries
  const captureRpcErrorForUserDql = useCallback(
    (error: unknown, context: Record<string, unknown>) => {
      const isBadUserQuery =
        context.errorType === 'dql_error' && context.httpStatus === 400
      captureRpcError(
        error,
        isBadUserQuery ? { ...context, level: 'debug' } : context,
      )
    },
    [captureRpcError],
  )

  const executeUserDqlQueryThroughRpc = useCallback(
    (query: string, args?: ExecuteDQLParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeDqlQuery(query, appDomain, appId, accessToken, args, {
            onError: captureRpcErrorForUserDql,
          })
        : Promise.resolve({
            queryType: 'unknown',
            items: [],
            error: {},
            warnings: [],
          } as DQLHTTPResponse)
    },
    [appId, appDomain, captureRpcErrorForUserDql],
  )

  const executeRemoteQueryThroughRpc = useCallback(
    (query: string, args?: ExecuteDQLParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeRemoteQuery(query, appDomain, appId, accessToken, args, {
            onError: captureRpcError,
          })
        : Promise.resolve({
            result: [],
            error: {},
          } as RemoteQueryHTTPResponse)
    },
    [appId, appDomain, captureRpcError],
  )

  return {
    rpcAccessToken,
    refreshToken: renewRpcToken,
    isEmptyCollection: isEmptyCollectionThroughRpc,
    getCollectionDocumentCount: getCollectionDocumentCountThroughRpc,
    attachmentBaseURL,
    downloadAttachment: downloadAttachmentThroughRpc,
    uploadAttachment: uploadAttachmentThroughRpc,
    executeDqlQuery: executeDqlQueryThroughRpc,
    executeUserDqlQuery: executeUserDqlQueryThroughRpc,
    executeRemoteQuery: executeRemoteQueryThroughRpc,
    executeLegacyFindQuery: executeLegacyFindQueryThroughRpc,
    executeLegacyFindByIdQuery: executeLegacyFindByIdQueryThroughRpc,
    executeLegacyUpdate: executeLegacyUpdateThroughRpc,
    executeLegacyUpsert: executeLegacyUpsertThroughRpc,
    executeLegacyRemove: executeLegacyRemoveThroughRpc,
    error,
  }
}

export default useRpcClient
